// InvitationTimer — auto-decrementing countdown until invite expires.
//
// Per W2 §4.3 step 2 Sub-flow A: spec requires "剩餘 X 天 Y 小時" line that
// decrements in real time.  We tick at 1-minute resolution (60s) which is
// the smallest granularity users care about.

import { useEffect, useState } from "react";

interface InvitationTimerProps {
  expiresAt: string;
  /** Optional override; defaults to the i18n labels. */
  onExpire?: () => void;
}

export function InvitationTimer({ expiresAt, onExpire }: InvitationTimerProps) {
  const [now, setNow] = useState(() => Date.now());

  // Tick at 60s resolution — see W2 §4.3 step 2 Sub-flow A.
  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const targetMs = Date.parse(expiresAt);
  const remainingMs = Number.isFinite(targetMs) ? targetMs - now : Number.NaN;

  // Fire onExpire when we cross zero.  Always called in the same order,
  // regardless of the validity branch below.
  useEffect(() => {
    if (Number.isFinite(remainingMs) && remainingMs <= 0) {
      onExpire?.();
    }
  }, [remainingMs, onExpire]);

  if (!Number.isFinite(targetMs)) {
    return (
      <span data-testid="invite-timer" data-status="invalid">
        過期時間無效
      </span>
    );
  }
  if (remainingMs <= 0) {
    return (
      <span data-testid="invite-timer" data-status="expired">
        已過期
      </span>
    );
  }

  const totalMinutes = Math.floor(remainingMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  return (
    <span data-testid="invite-timer" data-status="active" data-remaining-minutes={totalMinutes}>
      剩餘 {days > 0 ? `${days} 天 ` : ""}
      {hours > 0 ? `${hours} 小時 ` : ""}
      {minutes} 分鐘
    </span>
  );
}
